import React, { useState, useEffect } from "react";
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Switch, ScrollView, Modal } from "react-native";
import { RouteProp, useRoute } from "@react-navigation/native";
import { observer } from "mobx-react";
import { pushStore } from "../stores/PushStore";
import { postStore } from "../stores/PostStore";
import { IPush } from "@interfaces/ipush";
import { NotificacaoType, ordemTiposNotificacao } from "@interfaces/notificationType";
import { EnumRoutesAppWeb } from "../enum/EnumRoutesAppWeb";
import { Theme } from "../enum/Theme";
import { goBack } from "../services/NavigationService";
import { uuidv4 } from "@utils/idUtils";
import SpaceH from "@comps/SpaceH";

type PushFormRouteProp = RouteProp<{ [EnumRoutesAppWeb.createEditPush]: { push?: IPush } }, typeof EnumRoutesAppWeb.createEditPush>;

const PushForm = observer(() => {
  const route = useRoute<PushFormRouteProp>();
  const push = route.params?.push;

  const [title, setTitle] = useState(push?.title || "");
  const [postId, setPostId] = useState(push?.post_id || "");
  const [type, setType] = useState<NotificacaoType>(push?.type || ordemTiposNotificacao[0]);
  const [active, setActive] = useState(push?.active ?? true);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [postSearchText, setPostSearchText] = useState("");

  // Carrega os posts quando o componente monta
  useEffect(() => {
    postStore.fetchPosts();
  }, []);

  const getTypeLabel = (type: NotificacaoType) => {
    const typeLabels: Record<NotificacaoType, string> = {
      'mamada_atrasada': 'Mamada Atrasada',
      'soneca_atrasada': 'Soneca Atrasada',
      'sono_noturno_atrasado': 'Sono Noturno Atrasado',
      'sono': 'Sono',
      'mamada': 'Mamada',
      'sono_noturno': 'Sono Noturno',
      'soneca': 'Soneca',
      'falta': 'Falta',
      'disciplina': 'Disciplina',
      'soneca_curta': 'Soneca Curta',
      'despertar_frequente': 'Despertar Frequente',
      'parabens': 'Parabéns',
      'reminder': 'Lembrete'
    };
    return typeLabels[type] || type;
  };

  const getSelectedPostTitle = () => {
    const post = postStore.posts.find(p => p.id === postId);
    return post ? post.title : 'Selecionar Post';
  };

  const getSelectedTypeLabel = () => {
    return getTypeLabel(type);
  };

  const getFilteredPosts = () => {
    if (!postSearchText.trim()) {
      return postStore.posts;
    }
    return postStore.posts.filter(post => 
      post.title.toLowerCase().includes(postSearchText.toLowerCase())
    );
  };

  const handleSave = async () => {
    if (!title.trim()) {
      alert("Por favor, preencha o título");
      return;
    }

    if (!postId.trim()) {
      alert("Por favor, selecione um post");
      return;
    }

    const data: IPush = {
      id: push?.id || uuidv4(),
      title: title.trim(),
      post_id: postId,
      type,
      active,
      createdAt: push?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    await pushStore.savePush(data);
    goBack();
  };

  return (
    <ScrollView style={styles.container}>
      <Text style={styles.title}>{push ? "Editar Push" : "Novo Push"}</Text>

      <Text style={styles.section}>Título:</Text>
      <TextInput
        placeholder="Digite o título do push"
        value={title}
        onChangeText={setTitle}
        style={styles.input}
      />

      <Text style={styles.section}>Post Vinculado:</Text>
      <TouchableOpacity
        style={styles.postSelector}
        onPress={() => setShowPostModal(true)}
      >
        <Text style={styles.postSelectorText}>{getSelectedPostTitle()}</Text>
      </TouchableOpacity>

      <Text style={styles.section}>Tipo de Notificação:</Text>
      <TouchableOpacity
        style={styles.typeSelector}
        onPress={() => setShowTypeModal(true)}
      >
        <Text style={styles.typeSelectorText}>{getSelectedTypeLabel()}</Text>
      </TouchableOpacity>

      <View style={styles.switchContainer}>
        <Text>Push Ativo:</Text>
        <Switch value={active} onValueChange={setActive} />
      </View>

      <TouchableOpacity style={styles.saveButton} onPress={handleSave}>
        <Text style={styles.saveButtonText}>Salvar</Text>
      </TouchableOpacity>

      {/* Modal de Seleção de Post */}
      <Modal visible={showPostModal} animationType="slide">
        <ScrollView style={styles.modalContainer}>
          <Text style={styles.modalTitle}>Selecionar Post</Text>
          <SpaceH />
          
          <TextInput
            placeholder="Buscar post..."
            value={postSearchText}
            onChangeText={setPostSearchText}
            style={styles.searchInput}
          />
          <SpaceH />
          
          {getFilteredPosts().map((post) => (
            <TouchableOpacity
              key={post.id}
              style={[
                styles.postButton,
                postId === post.id && styles.postButtonActive
              ]}
              onPress={() => {
                setPostId(post.id);
                setShowPostModal(false);
                setPostSearchText(""); // Limpa a busca ao selecionar
              }}
            >
              <Text style={[
                styles.postButtonText,
                postId === post.id && styles.postButtonTextActive
              ]}>
                {post.title}
              </Text>
            </TouchableOpacity>
          ))}
          
          {getFilteredPosts().length === 0 && postSearchText.trim() && (
            <Text style={styles.noResultsText}>Nenhum post encontrado</Text>
          )}
          
          <SpaceH />
          <TouchableOpacity
            style={styles.closeButton}
            onPress={() => {
              setShowPostModal(false);
              setPostSearchText(""); // Limpa a busca ao fechar
            }}
          >
            <Text style={styles.closeButtonText}>Fechar</Text>
          </TouchableOpacity>
        </ScrollView>
      </Modal>

      {/* Modal de Seleção de Tipo */}
      <Modal visible={showTypeModal} animationType="slide">
        <ScrollView style={styles.modalContainer}>
          <Text style={styles.modalTitle}>Selecionar Tipo de Notificação</Text>
          <SpaceH />
          
          {ordemTiposNotificacao.map((notificationType) => (
            <TouchableOpacity
              key={notificationType}
              style={[
                styles.typeButton,
                type === notificationType && styles.typeButtonActive
              ]}
              onPress={() => {
                setType(notificationType);
                setShowTypeModal(false);
              }}
            >
              <Text style={[
                styles.typeButtonText,
                type === notificationType && styles.typeButtonTextActive
              ]}>
                {getTypeLabel(notificationType)}
              </Text>
            </TouchableOpacity>
          ))}
          
          <SpaceH />
          <TouchableOpacity
            style={styles.closeButton}
            onPress={() => setShowTypeModal(false)}
          >
            <Text style={styles.closeButtonText}>Fechar</Text>
          </TouchableOpacity>
        </ScrollView>
      </Modal>
    </ScrollView>
  );
});

export default PushForm;

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20, backgroundColor: "#F2F2F2" },
  title: { fontSize: 22, fontWeight: "bold", marginBottom: 20 },
  section: { marginTop: 15, fontWeight: "bold", marginBottom: 5 },
  input: { borderWidth: 1, borderColor: "#ccc", borderRadius: 5, padding: 10, marginBottom: 10 },
  postSelector: { 
    borderWidth: 1, 
    borderColor: "#ccc", 
    borderRadius: 5, 
    padding: 10, 
    marginBottom: 15,
    backgroundColor: "#fff"
  },
  postSelectorText: { color: "#333" },
  postButton: { 
    padding: 10, 
    borderWidth: 1, 
    borderColor: "#ccc", 
    borderRadius: 5, 
    marginBottom: 5,
    backgroundColor: "#fff"
  },
  postButtonActive: { backgroundColor: Theme.foreground },
  postButtonText: { color: "#333" },
  postButtonTextActive: { color: "#fff" },
  modalContainer: { flex: 1, padding: 20, backgroundColor: "#F2F2F2" },
  modalTitle: { fontSize: 18, fontWeight: "bold", marginBottom: 20, textAlign: "center" },
  searchInput: { 
    borderWidth: 1, 
    borderColor: "#ccc", 
    borderRadius: 5, 
    padding: 10, 
    marginBottom: 10,
    backgroundColor: "#fff"
  },
  noResultsText: { 
    textAlign: "center", 
    color: "#666", 
    fontStyle: "italic", 
    marginTop: 20 
  },
  closeButton: { backgroundColor: Theme.foreground, padding: 15, borderRadius: 5, marginTop: 20 },
  closeButtonText: { color: "#fff", textAlign: "center", fontWeight: "bold" },
  typeSelector: { 
    borderWidth: 1, 
    borderColor: "#ccc", 
    borderRadius: 5, 
    padding: 10, 
    marginBottom: 15,
    backgroundColor: "#fff"
  },
  typeSelectorText: { color: "#333" },
  typeButton: { 
    padding: 10, 
    borderWidth: 1, 
    borderColor: "#ccc", 
    borderRadius: 5, 
    marginBottom: 5,
    backgroundColor: "#fff"
  },
  typeButtonActive: { backgroundColor: Theme.foreground },
  typeButtonText: { color: "#333" },
  typeButtonTextActive: { color: "#fff" },
  switchContainer: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 20 },
  saveButton: { backgroundColor: Theme.foreground, padding: 15, borderRadius: 5 },
  saveButtonText: { color: "#fff", textAlign: "center", fontWeight: "bold" },
});
